/* ROBBOX navigation: desktop mega menu + mobile drawer. No dependencies. */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 1024px)');
  const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const visibleFocusables = (root) =>
    [...root.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[inert]') && el.getClientRects().length);

  /* ------------------------------------------------------------------------
     Desktop mega menu
     ------------------------------------------------------------------------ */

  class MegaMenu {
    constructor(item) {
      this.item = item;
      this.trigger = item.querySelector('[data-rbx-mega-trigger]');
      this.panel = item.querySelector('[data-rbx-mega]');
      this.overlay = item.querySelector('[data-rbx-mega-overlay]');
      this.wrapper = item.closest('[data-site-navigation]') || item.parentElement;
      this.isOpen = false;
      this.openTimer = null;
      this.closeTimer = null;
      this.abort = new AbortController();

      const opts = { signal: this.abort.signal };

      item.addEventListener('mouseenter', () => this.hoverOpen(), opts);
      item.addEventListener('mouseleave', () => this.hoverClose(), opts);
      // Keyboard focus opens the panel; pointer focus is left to hover/tap handling below.
      this.trigger.addEventListener('pointerdown', () => (this.pointerFocus = true), opts);
      this.trigger.addEventListener(
        'focus',
        () => {
          if (DESKTOP.matches && !this.pointerFocus) this.open();
          this.pointerFocus = false;
        },
        opts,
      );

      // Touch / pen: the first tap opens the panel, a second tap follows the link.
      this.trigger.addEventListener(
        'click',
        (event) => {
          if (!DESKTOP.matches || HOVER.matches) return;
          if (!this.isOpen) {
            event.preventDefault();
            this.open();
          }
        },
        opts,
      );

      item.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !this.isOpen) return;
          event.stopPropagation();
          this.close();
          // Keep the panel closed even though the trigger regains focus.
          this.suppressFocusOpen();
          this.trigger.focus({ preventScroll: true });
        },
        opts,
      );

      item.addEventListener(
        'focusout',
        (event) => {
          if (!item.contains(event.relatedTarget)) this.close();
        },
        opts,
      );

      this.overlay?.addEventListener('click', () => this.close(), opts);
      document.addEventListener(
        'click',
        (event) => {
          if (this.isOpen && !item.contains(event.target)) this.close();
        },
        opts,
      );
      DESKTOP.addEventListener('change', () => this.close(), opts);
    }

    suppressFocusOpen() {
      this.focusSuppressed = true;
      requestAnimationFrame(() => (this.focusSuppressed = false));
    }

    hoverOpen() {
      if (!DESKTOP.matches || !HOVER.matches) return;
      clearTimeout(this.closeTimer);
      clearTimeout(this.openTimer);
      // A short intent delay stops the menu flashing when the cursor merely crosses the nav.
      this.openTimer = setTimeout(() => this.open(), this.isOpen ? 0 : 90);
    }

    hoverClose() {
      if (!HOVER.matches) return;
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      // Grace period lets the cursor travel from the trigger into the panel without flicker.
      this.closeTimer = setTimeout(() => {
        if (!this.item.contains(document.activeElement)) this.close();
      }, 220);
    }

    open() {
      if (this.isOpen || this.focusSuppressed) return;
      clearTimeout(this.closeTimer);
      const top = this.wrapper.getBoundingClientRect().bottom;
      this.item.style.setProperty('--rbx-mega-top', `${Math.max(0, Math.round(top))}px`);
      this.panel.inert = false;
      this.panel.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }

    close() {
      clearTimeout(this.openTimer);
      if (!this.isOpen) return;
      this.panel.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
      this.panel.inert = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.isOpen = false;
    }

    destroy() {
      this.close();
      this.abort.abort();
    }
  }

  /* ------------------------------------------------------------------------
     Mobile drawer (modal dialog with sliding sub-views)
     ------------------------------------------------------------------------ */

  /* ------------------------------------------------------------------------
     Carousel (featured banners in the drawer)
     ------------------------------------------------------------------------ */

  class Carousel {
    constructor(el, signal) {
      this.track = el.querySelector('[data-rbx-carousel-track]');
      this.slides = [...el.querySelectorAll('[data-rbx-carousel-slide]')];
      this.prev = el.querySelector('[data-rbx-carousel-prev]');
      this.next = el.querySelector('[data-rbx-carousel-next]');
      const opts = { signal };

      this.prev.addEventListener('click', () => this.go(-1), opts);
      this.next.addEventListener('click', () => this.go(1), opts);
      this.track.addEventListener('scroll', () => this.queueUpdate(), { ...opts, passive: true });
      window.addEventListener('resize', () => this.queueUpdate(), opts);
      this.update();
    }

    index() {
      const width = this.track.clientWidth || 1;
      return Math.round(this.track.scrollLeft / width);
    }

    go(step) {
      const target = this.slides[Math.min(this.slides.length - 1, Math.max(0, this.index() + step))];
      if (!target) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    }

    queueUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= max - 2;
      // Moving focus off a button as it disables would drop keyboard users to <body>.
      if (atStart && document.activeElement === this.prev) this.next.focus({ preventScroll: true });
      if (atEnd && document.activeElement === this.next) this.prev.focus({ preventScroll: true });
      this.prev.disabled = atStart;
      this.next.disabled = atEnd;
      // Off-screen slides stay out of the tab order.
      const current = this.index();
      this.slides.forEach((slide, i) => (slide.inert = i !== current));
    }
  }

  class Drawer {
    constructor(drawer, toggle) {
      this.drawer = drawer;
      this.toggle = toggle;
      this.panel = drawer.querySelector('[data-rbx-panel]');
      this.root = drawer.querySelector('[data-rbx-view="root"]');
      this.stack = [this.root];
      this.openers = [];
      this.isOpen = false;
      this.abort = new AbortController();

      const opts = { signal: this.abort.signal };

      // Fixed-position UI must not be trapped inside the sticky header's stacking context.
      document.body.appendChild(drawer);

      if (toggle) {
        toggle.setAttribute('aria-controls', drawer.id);
        toggle.setAttribute('aria-expanded', 'false');
        // Capture phase on window runs before the theme's own listener on the toggle,
        // so the original drawer never opens while this one is enabled.
        window.addEventListener(
          'click',
          (event) => {
            if (!event.target.closest || event.target.closest('[data-menu-toggle]') !== toggle) return;
            event.preventDefault();
            event.stopPropagation();
            this.open();
          },
          { capture: true, signal: this.abort.signal },
        );
      }

      drawer.addEventListener(
        'click',
        (event) => {
          const target = event.target.closest('[data-rbx-close], [data-rbx-open-view], [data-rbx-back]');
          if (!target) return;
          if (target.matches('[data-rbx-close]')) this.close();
          else if (target.matches('[data-rbx-back]')) this.back();
          else this.openView(target);
        },
        opts,
      );

      drawer.addEventListener('keydown', (event) => this.onKeydown(event), opts);
      DESKTOP.addEventListener('change', () => DESKTOP.matches && this.close(), opts);
      window.addEventListener(
        'cartcount:update',
        (event) => this.updateCartCount(event.detail?.item_count),
        opts,
      );

      drawer.querySelectorAll('[data-rbx-carousel]').forEach((el) => new Carousel(el, this.abort.signal));
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.lockScroll();
      this.drawer.inert = false;
      this.drawer.classList.add('is-open');
      this.drawer.querySelectorAll('[data-rbx-carousel-track]').forEach((track) => track.dispatchEvent(new Event('scroll')));
      this.toggle?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.panel.focus({ preventScroll: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      this.drawer.inert = true;
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.unlockScroll();
      (this.toggle || this.lastFocus)?.focus({ preventScroll: true });

      // Reset to the root view once the slide-out has finished.
      const reset = () => {
        while (this.stack.length > 1) this.back({ instant: true });
      };
      if (this.stack.length > 1) setTimeout(reset, this.duration());
    }

    openView(opener) {
      const view = this.drawer.querySelector(`#${CSS.escape(opener.dataset.rbxOpenView)}`);
      if (!view) return;
      const current = this.stack[this.stack.length - 1];
      current.classList.add('is-behind');
      current.classList.remove('is-active');
      current.inert = true;
      view.inert = false;
      view.classList.add('is-active');
      view.querySelector('.rbx-view__scroll').scrollTop = 0;
      opener.setAttribute('aria-expanded', 'true');
      this.stack.push(view);
      this.openers.push(opener);
      view.querySelector('[data-rbx-back]')?.focus({ preventScroll: true });
    }

    back({ instant = false } = {}) {
      if (this.stack.length < 2) return;
      const view = this.stack.pop();
      const opener = this.openers.pop();
      const previous = this.stack[this.stack.length - 1];
      if (instant) view.style.transition = previous.style.transition = 'none';
      view.classList.remove('is-active');
      view.inert = true;
      previous.classList.remove('is-behind');
      previous.classList.add('is-active');
      previous.inert = false;
      opener?.setAttribute('aria-expanded', 'false');
      if (instant) {
        // Flush styles, then restore transitions.
        void view.offsetWidth;
        view.style.transition = previous.style.transition = '';
      } else {
        // The previous view kept its own scroll position; focus returns to the item that opened the view.
        opener?.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (this.stack.length > 1) this.back();
        else this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = visibleFocusables(this.panel);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      this.scrollY = window.scrollY;
      const body = document.body;
      const gap = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.classList.add('rbx-scroll-locked');
      // position:fixed keeps iOS Safari from scrolling the page behind the drawer.
      Object.assign(body.style, {
        position: 'fixed',
        top: `-${this.scrollY}px`,
        left: '0',
        right: '0',
        paddingRight: gap ? `${gap}px` : '',
      });
    }

    unlockScroll() {
      document.documentElement.classList.remove('rbx-scroll-locked');
      Object.assign(document.body.style, { position: '', top: '', left: '', right: '', paddingRight: '' });
      window.scrollTo({ top: this.scrollY || 0, behavior: 'instant' });
    }

    updateCartCount(count) {
      const badge = this.drawer.querySelector('[data-rbx-cart-count]');
      if (!badge || typeof count !== 'number') return;
      badge.textContent = count;
      badge.hidden = count === 0;
    }

    duration() {
      return parseFloat(getComputedStyle(this.drawer).getPropertyValue('--rbx-dur')) || 250;
    }

    destroy() {
      if (this.isOpen) this.close();
      this.abort.abort();
      this.drawer.remove();
    }
  }

  /* ------------------------------------------------------------------------
     Init + theme editor support
     ------------------------------------------------------------------------ */

  const instances = new Map();

  const init = (scope = document) => {
    scope.querySelectorAll('[data-rbx-mega-item]').forEach((item) => {
      if (!instances.has(item)) instances.set(item, new MegaMenu(item));
    });
    scope.querySelectorAll('[data-rbx-drawer]').forEach((drawer) => {
      if (instances.has(drawer)) return;
      const section = document.getElementById(`shopify-section-${drawer.dataset.sectionId}`);
      const toggle = (section || document).querySelector('[data-menu-toggle]');
      instances.set(drawer, new Drawer(drawer, toggle));
    });
  };

  const destroy = (sectionId) => {
    instances.forEach((instance, el) => {
      const owner = el.dataset.sectionId || el.closest('.shopify-section')?.id.replace('shopify-section-', '');
      if (owner !== sectionId) return;
      instance.destroy();
      instances.delete(el);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:unload', (event) => destroy(event.detail.sectionId));

    // Selecting a ROBBOX block in the editor previews it in the matching menu.
    // Stopped here so the theme's meganav handler doesn't look for a block it doesn't know.
    window.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!event.target.closest?.('.rbx-scope')) return;
        event.stopImmediatePropagation();
        const item = event.target.closest('[data-rbx-mega-item]');
        if (item && DESKTOP.matches) {
          instances.get(item)?.open();
          return;
        }
        // Cards and promos render in both menus; below desktop, preview them in the drawer.
        const drawer = event.target.closest('[data-rbx-drawer]') || document.querySelector('[data-rbx-drawer]');
        instances.get(drawer)?.open();
      },
      true,
    );
    window.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (!event.target.closest?.('.rbx-scope')) return;
        event.stopImmediatePropagation();
      },
      true,
    );
  }
})();
